<template>
    <auth-layout>
        <skeleton-loader v-if="loading" type="auth-action" />

        <div v-else class="invitation">
            <div class="invitation__icon">
                <v-icon size="34" color="primary">mdi-email-check-outline</v-icon>
            </div>

            <template v-if="invitation">
                <div class="invitation__title">{{ $t('entrance.coach_company_invitation_title') }}</div>
                <div class="invitation__subtitle">
                    {{ $t('entrance.coach_company_invitation_subtitle', { company: invitation.company.name }) }}
                </div>

                <div class="invitation__actions">
                    <v-btn
                        color="error" outlined large block class="text-none font-weight-bold"
                        :loading="rejectLoading" @click="reject">
                        {{ $t('common.reject') }}
                    </v-btn>
                    <v-btn
                        color="primary" depressed large block class="text-none font-weight-bold black--text"
                        :loading="acceptLoading" @click="accept">
                        {{ $t('common.accept') }}
                    </v-btn>
                </div>
            </template>

            <div v-else class="invitation__subtitle invitation__subtitle--error">
                {{ $t('entrance.coach_company_invitation_error') }}
            </div>

            <logout-button outlined color="txtSecondary" large block class="invitation__logout" />
        </div>
    </auth-layout>
</template>

<script>
import store from '@/store'
import AuthLayout from "@/components/entrance/AuthLayout";
import LogoutButton from "@/components/common/inputs/LogoutButton";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import { db, FieldValue } from "@/utils/Firebase";

export default {
    name: "CoachCompanyInvitation",
    components: { AuthLayout, SkeletonLoader, LogoutButton },
    beforeRouteEnter(to, from, next) {
        const account = store.state.account
        const eligible = account?.accountType === 'coach_company' && account?.firstLogin === true
        return eligible ? next() : next({ name: 'splash', replace: true })
    },
    data() {
        return {
            loading: false,
            invitation: null,
            rejectLoading: false,
            acceptLoading: false,
        }
    },
    mounted() {
        const vm = this
        vm.loadInvitation()
    },
    methods: {
        async loadInvitation() {
            const vm = this
            await vm.withLoading(async () => {
                let invitationRef = await db.collection('coachInvitations')
                    .where('email', '==', vm.account.email)
                    .where('status', '==', 'pending')
                    .limit(1).get()

                let invitation = invitationRef.docs[0] || null

                vm.invitation = !invitation ? null : {
                    ...invitation.data(),
                    id: invitation.id,
                }
            })
        },
        async reject() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('coachInvitations').doc(vm.invitation.id).update({
                    status: 'rejected',
                })

                const payload = {
                    accountType: null,
                    firstLogin: false,
                }

                const currentLink = vm.account?.coachSubscription || null
                if (currentLink?.companyId === vm.invitation.company.id) {
                    if (currentLink.id) await db.collection('subscriptions').doc(currentLink.id).delete()
                    payload.coachSubscription = null
                }

                await db.collection('users').doc(vm.currentUser.uid).update(payload)

                vm.replaceRoute({ name: 'select-profile' })
            }, 'rejectLoading')
        },
        async accept() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('coachInvitations').doc(vm.invitation.id).update({
                    status: 'accepted',
                })

                let subscription = await db.collection('subscriptions').add({
                    companyId: vm.invitation.company.id,
                    coachId: vm.currentUser.uid,
                    paused: false,
                    createdAt: FieldValue.serverTimestamp()
                })

                await db.collection('users').doc(vm.currentUser.uid).update({
                    firstLogin: false,
                    coachSubscription: {
                        companyDescription: vm.invitation.company.description,
                        companyName: vm.invitation.company.name,
                        companyId: vm.invitation.company.id,
                        id: subscription.id,
                        paused: false,
                    }
                })

                await db.collection('users').doc(vm.invitation.company.id).update({
                    activeCoaches: FieldValue.increment(1)
                })

                vm.replaceRoute({ name: 'coach.home' })
            }, 'acceptLoading')
        },
    }
}
</script>

<style scoped>
.invitation {
    width: 100%;
    max-width: 360px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.invitation__icon {
    width: 76px;
    height: 76px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    background: rgba(204, 255, 51, 0.1);
    border: 1px solid rgba(204, 255, 51, 0.28);
}

.invitation__title {
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.15;
    color: var(--v-txtPrimary-base);
}

.invitation__subtitle {
    margin-top: 12px;
    font-size: var(--fs-body);
    line-height: 1.5;
    color: var(--v-txtSecondary-base);
}

.invitation__subtitle--error {
    color: var(--v-error-base);
}

.invitation__actions {
    width: 100%;
    display: flex;
    gap: 12px;
    margin-top: 32px;
}

.invitation__logout {
    margin-top: 12px;
}
</style>
